import * as React from "react";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import {Routine} from "redux-saga-routines";
import {Action, ActionFunctionAny} from "redux-actions";
import styled from "styled-components";
import { Device, Transaction } from "./Store/types";
import {
  initialize,
  getPositions,
  getAllDevices,
  getPositionsUuid
} from "./Store/routines";
import { ResetState, resetState, GenerateMapUuid, generateMapUuid } from "./Store/actions";
import {v4 as uuidv4} from 'uuid';
import { H1 } from "../Header";
import LogTable from "./Log/table";
import Map from "./Map/map";
import Dropdown from "../../Elements/Dropdown/Dropdown";
import {Option} from "../DefaultSettings/Settings/FieldWrapper";
import Footer from "./footer";
import { ActionButtons } from "./header";
import MapConsent from "./mapConsent";
import { Policies } from "../Policy/types";
import TrackHeader from "./trackHeader";
import { AppState } from "@Safemate/Store/types";
import { useParams } from "react-router";
import { SiteTypeEnum } from "@Safemate/SiteTypeEnum";

import Spinner from "../Spinner";
import { AllPermissions, PolicyContext } from "@Safemate/Policy/Provider";
import { PolicyNameEnum } from "@Safemate/PolicyNameEnum";
import DeviceHeader, { StyledSpan } from "@Safemate/DeviceHeader";
import {Capabilities} from "@Safemate/Settings/Store/types";

export enum MapType{
  ALL,
  SINGLE
}

const Wrap = styled.div`
  position: relative;
`;

const DropdownWrapper = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  font-size: 14pt;
  @media (max-width: 600px) {
    margin-right: auto;
    margin-left: 0;
    margin-top: 10px;
  }
`;

export const DropdownLabel = styled.span`
  margin-right: 10px;
  white-space: nowrap;
`;

const HeaderRow = styled.div`
  margin-bottom: 30px;
  display: flex;
  align-items: center;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

interface LogProps{
  transactions: Transaction[],
  positionCount: number;
  getPositions: Routine<ActionFunctionAny<Action<any>>>;
  getPositionsUuid: Routine<ActionFunctionAny<Action<any>>>;
  initialize: Routine<ActionFunctionAny<Action<any>>>;
  device: Device;
  policies: Policies;
  resetState: ResetState;
  generateMapUuid: GenerateMapUuid;
  initialized: boolean;
  isTrackerOnly: boolean;
  noMoreTransactionsToFetch: boolean;
  getAllDevices: Routine<ActionFunctionAny<Action<any>>>;
  currentContext: SiteTypeEnum;
  mapType: MapType;
  uuid: string;
  displayGraph: boolean;
  capabilities: Map<number, Capabilities>;
}

const logStateToProps = ({ appData: {isTrackerOnly, currentContext, capabilities}, transactionLog: {device, transaction: { transactions, positionCount, noMoreTransactionsToFetch, uuid, displayGraph }, initialization: { initialized }, policy: { policies }}}: AppState) => {
  return {
    transactions,
    positionCount,
    noMoreTransactionsToFetch,
    initialized,
    device,
    policies,
    isTrackerOnly,
    currentContext,
    uuid,
    displayGraph,
    capabilities
  }
}

const mapDispatchToProps = {
  initialize,
  getAllDevices,
  getPositions,
  getPositionsUuid,
  resetState,
  generateMapUuid,
}

export interface LogParams{
  id: string;
}

const Log = connect(logStateToProps, mapDispatchToProps)(
  ({ initialize, mapType, getPositions, getPositionsUuid, resetState, positionCount, getAllDevices, device, generateMapUuid,  uuid, isTrackerOnly,  initialized, noMoreTransactionsToFetch, capabilities }: LogProps) => {
    const { formatMessage } = useIntl();

    const { id } = useParams<LogParams>();
    const deviceId = parseInt(id);

    const permissions = React.useContext(PolicyContext) as Map<PolicyNameEnum, AllPermissions>;
    const logPermission = permissions.get(PolicyNameEnum.DEVICE_TRANSACTION_LOG).generalPermissions.all;

    React.useEffect(() => {
      generateMapUuid(uuidv4());
      initialize({deviceId, mapType, logPermission, uuid});

      let transactionInterval: NodeJS.Timeout;
      let positionInterval: NodeJS.Timeout;
      let devicesInterval: NodeJS.Timeout;


      if(mapType === MapType.SINGLE){
        if(!isTrackerOnly && logPermission){
          setTimeout(() => {
            positionInterval = setInterval(() => {
              getPositions({deviceId});
            }, 20000);
          }, 1000)
        }
      } else if(mapType === MapType.ALL){
        devicesInterval = setInterval(() => {
          getAllDevices();
        }, 20000);
      }
  
      return () => {
        // Cleanup polling
        if(mapType === MapType.SINGLE){
          setTimeout(() => {
            clearTimeout(positionInterval);
          }, 1000)
        }
        else if(mapType === MapType.ALL){
          clearTimeout(devicesInterval)
        }
        resetState();
      }

    }, [])



    if(!initialized){
      return(
        <Wrap>
          <Spinner show message={formatMessage({id: "initData", defaultMessage: "Initialisering av data"})}/>
        </Wrap>
      )
    }

    const capability = capabilities.get(device.dehaId);

    return(
      <React.Fragment>
        <TrackHeader/>
        {!isTrackerOnly && mapType === MapType.SINGLE &&
          <DeviceHeader 
            dehaId={device.dehaId}
            name={device.deviceName} 
            deviId={device.id} 
            custId={device.custId} 
            isTwin={device.minionTwin} 
            serialNumber={device.serialNumber}
            hasRecipient={device.hasRecipient}
            lastMsgIn={device.lastMsgIn}
          >
            <>
              <StyledSpan>
                {` ${formatMessage({ id: "mapFor", defaultMessage: "Kart for" })} `}
              </StyledSpan>
            </>
        </DeviceHeader>}
        {initialized && (mapType === MapType.ALL || capability.positioning) && <Map mapType={mapType} deviceId={deviceId} />}
        {mapType === MapType.ALL ?
        <HeaderRow>
          {!isTrackerOnly && 
          <H1>
            {`${formatMessage({id: "transactionLogFor", defaultMessage: "Transaksjonslogg for"})} ${device.devices.length}`}
          </H1>}
        </HeaderRow>
        : capability.positioning &&
        <HeaderRow>
          <ActionButtons mapType={mapType}/>
          <DropdownWrapper>
            <DropdownLabel>
              {formatMessage({id: "numPositions", defaultMessage: "Antall posisjoner vist"})}
            </DropdownLabel>
            <Dropdown
              initial={positionCount}
              options={[
                {text: "1", value: 1},
                {text: "10", value: 10},
                {text: "25", value: 25},
                {text: "50", value: 50}
              ]}
              onChange={(option) => {
                const selected = (option as Option).value;
                getPositions({
                  count: selected,
                  deviceId: id,
                  uuid
                })}
              }
            />
          </DropdownWrapper>
        </HeaderRow>}
        <LogTable mapType={mapType}/>
        <Footer mapType={mapType} deviceId={deviceId} noMoreToFetch={noMoreTransactionsToFetch}/>
        <MapConsent/>
      </React.Fragment>
    )
  })


export default Log;
